import * as swcHelpers from '@swc/helpers';
import { createSelector } from 'reselect';
import { FeatureToggleSelectors, statusSuccess, IntlSelectors, uninitState, successState } from '@grandvisionhq/state';
import { isDefined } from '@grandvisionhq/utils';
import { isNewPrescriptionAndUpload } from '../../utils/is-new-prescription-upload.js';
import { isPupillaryDistance, isAxis, isCylinder } from '../../utils/parameter-type-guards.js';
import { getOpticalParameters } from '../bundles/selectors.internal.js';
import { getConfigurator } from '../configurator/selectors.internal.js';
import { RX_SCOPE, PD_SCOPE } from '../constants.js';
import { convertToLensTypeInfo, convertToPackageInfo, convertToLensAddOnInfo } from '../helpers.js';
import { lookupParentLensType, getPackage, lookupParentAddOn } from '../products/selectors.internal.js';
import { isParameterValueValid } from './helpers.js';
import { getConfiguration, getLensTypeSku, getPackageSku, getAddOnSku, getPrescription as getPrescription$1 } from './selectors.internal.js';

const getPrescriptionType = createSelector(getConfiguration, (c)=>{
    return c === null || c === void 0 ? void 0 : c.prescriptionType;
});
const getIsPupillaryDistanceFileOptional = createSelector(FeatureToggleSelectors.getFeatureToggles, (c)=>{
    return c === null || c === void 0 ? void 0 : c.isPupillaryDistanceFileOptional;
});
const getIsRxAxisCylinderOptional = createSelector(FeatureToggleSelectors.getFeatureToggles, (c)=>{
    return c === null || c === void 0 ? void 0 : c.isRxAxisCylinderOptional;
});
const getValidationParameters = createSelector(getOpticalParameters, getPrescriptionType, getIsPupillaryDistanceFileOptional, getIsRxAxisCylinderOptional, (parameters, prescriptionType, isPDFileOptional, isAxisCylinderOptional)=>{
    if (!statusSuccess(parameters)) {
        return undefined;
    }
    const isOptionalParam = (param)=>{
        if (isPupillaryDistance(param) && isPDFileOptional && isNewPrescriptionAndUpload(prescriptionType)) {
            return true;
        }
        if (isAxisCylinderOptional && (isAxis(param) || isCylinder(param))) {
            return true;
        }
        return false;
    };
    return parameters.result.map((param)=>swcHelpers.objectSpread({}, param, {
            isOptional: isOptionalParam(param)
        })
    );
});
const getFormValidationParameters = createSelector((s)=>s
, getConfigurator, (state, config)=>{
    if (!(config === null || config === void 0 ? void 0 : config.frameSku)) {
        return undefined;
    }
    const lensTypeSku = getLensTypeSku(state, {
        frameSku: config.frameSku
    });
    if (!lensTypeSku) {
        return undefined;
    }
    return getValidationParameters(state, {
        frameSku: config.frameSku,
        lensTypeSku
    });
});
/**
 * The currently selected lens-type product for the given frame SKU
 */ const getSelectedLensType = createSelector(getLensTypeSku, IntlSelectors.getLocale, (s)=>s
, (lensTypeSku, locale, state)=>{
    if (!lensTypeSku || !locale) {
        return undefined;
    }
    const lensTypeProduct = lookupParentLensType(state, {
        lensTypeSku
    });
    if (!lensTypeProduct) {
        return undefined;
    }
    return convertToLensTypeInfo(lensTypeProduct, lensTypeSku, locale);
});
/**
 * The currently selected package product for the given frame SKU
 */ const getSelectedPackage = createSelector(getPackageSku, IntlSelectors.getLocale, (s)=>s
, (packageSku, locale, state)=>{
    if (!packageSku || !locale) {
        return undefined;
    }
    const packageProduct = getPackage(state, {
        packageSku
    });
    if (!packageProduct) {
        return undefined;
    }
    return convertToPackageInfo(packageProduct, packageSku, locale);
});
/**
 * The currently selected add-on product for the given frame SKU
 */ const getSelectedAddOn = createSelector(getAddOnSku, IntlSelectors.getLocale, (s)=>s
, (addOnSku, locale, state)=>{
    if (!addOnSku || !locale) {
        return;
    }
    const addOnProduct = lookupParentAddOn(state, {
        addOnSku
    });
    if (!addOnProduct) {
        return undefined;
    }
    return convertToLensAddOnInfo(addOnProduct, addOnSku, locale);
});
/**
 * The prescription parameter values as entered on the current frame / lens
 */ const getPrescription = createSelector(getPrescription$1, getLensTypeSku, getOpticalParameters, (prescription, lensTypeSku, parameters)=>{
    if (!lensTypeSku) {
        return uninitState();
    }
    if (!statusSuccess(parameters)) {
        return parameters;
    }
    return successState(swcHelpers.objectSpread({
        parameters: parameters.result
    }, prescription, {
        uploadReferences: prescription.uploadReferences || {}
    }));
});
var _additionalInfo;
/**
 * The prescription info as entered on the current frame / lens
 */ const getAdditionalPrescriptionInfoFormState = createSelector(getPrescription$1, (prescription)=>(_additionalInfo = prescription.additionalInfo) !== null && _additionalInfo !== void 0 ? _additionalInfo : {}
);
// Returns the values in b that aren't present in a
const diff = (a, b)=>new Set([
        ...b
    ].filter((i)=>!a.has(i)
    ))
;
/**
 * Get the validation state of the currently selected lens-type for the given frameSku
 */ const getManualPrescriptionValidationState = createSelector(getPrescription$1, getLensTypeSku, getValidationParameters, (prescription, lensTypeSku, parameters)=>{
    if (!lensTypeSku) {
        return undefined;
    }
    if (!parameters) {
        return undefined;
    }
    const parameterNames = new Set(parameters.map((p)=>p.name
    ));
    const leftHasExtra = diff(parameterNames, Object.keys(prescription.left)).size > 0;
    const rightHasExtra = diff(parameterNames, Object.keys(prescription.right)).size > 0;
    const initialValidationState = {
        isValid: !leftHasExtra && !rightHasExtra,
        left: {
            isValid: !leftHasExtra
        },
        right: {
            isValid: !rightHasExtra
        }
    };
    /**
     * The Axis and Cylinder fields depend on each other. If one is filled and the other is empty,
     * the empty field should be marked as invalid.
     */ const isAxisCylinderValid = (paramName, axisValue, cylinderValue)=>{
        const axisFilled = isDefined(axisValue) && axisValue !== 0;
        const cylinderFilled = isDefined(cylinderValue) && cylinderValue !== 0;
        if (paramName === 'axis' && !axisFilled && cylinderFilled) {
            return false;
        }
        if (paramName === 'cylinder' && !cylinderFilled && axisFilled) {
            return false;
        }
        return true;
    };
    return parameters.reduce((acc, param)=>{
        const leftValue = prescription.left[param.name];
        const rightValue = prescription.right[param.name];
        const isLeftValid = isParameterValueValid(param, leftValue) && isAxisCylinderValid(param.name, prescription.left.axis, prescription.left.cylinder);
        const isRightValid = isParameterValueValid(param, rightValue) && isAxisCylinderValid(param.name, prescription.right.axis, prescription.right.cylinder);
        const left = swcHelpers.objectSpread({}, acc.left, {
            isValid: acc.left.isValid && isLeftValid,
            [param.name]: isLeftValid
        });
        const right = swcHelpers.objectSpread({}, acc.right, {
            isValid: acc.right.isValid && isRightValid,
            [param.name]: isRightValid
        });
        return {
            isValid: acc.isValid && left.isValid && right.isValid,
            left,
            right
        };
    }, initialValidationState);
});
const getPdUploadValidationRequirement = createSelector(getPrescriptionType, getPrescription$1, getIsPupillaryDistanceFileOptional, (prescriptionType, prescription, isPDFileOptional)=>{
    // When in the manual entry + upload flow, if the user fills in the pupillary distance
    // for both eyes manually, they no longer need to upload their PD document
    if (isNewPrescriptionAndUpload(prescriptionType) && isPDFileOptional) {
        return {
            isRequired: !isDefined(prescription === null || prescription === void 0 ? void 0 : prescription.left.pupillaryDistance) || !isDefined(prescription === null || prescription === void 0 ? void 0 : prescription.right.pupillaryDistance)
        };
    }
    return {
        isRequired: true
    };
});
const getPrescriptionUploadValidationState = createSelector(getPrescription$1, getLensTypeSku, getPdUploadValidationRequirement, (prescription, lensTypeSku, pdUploadValidationRequirement)=>{
    var ref2, ref1;
    if (!lensTypeSku) {
        return undefined;
    }
    const { uploadReferences  } = prescription;
    const hasUploadedRx = !!((ref2 = (uploadReferences || {})[RX_SCOPE]) === null || ref2 === void 0 ? void 0 : ref2.storageToken);
    const hasUploadedPd = !!((ref1 = (uploadReferences || {})[PD_SCOPE]) === null || ref1 === void 0 ? void 0 : ref1.storageToken);
    const isRxValid = hasUploadedRx;
    const isPdValid = !pdUploadValidationRequirement.isRequired || hasUploadedPd;
    return {
        isValid: isRxValid && isPdValid,
        [RX_SCOPE]: {
            isValid: isRxValid
        },
        [PD_SCOPE]: {
            isValid: isPdValid
        }
    };
});
var ref;
const getVerificationAllowed = createSelector(getPrescription$1, (c)=>{
    return (ref = c === null || c === void 0 ? void 0 : c.verificationAllowed) !== null && ref !== void 0 ? ref : false;
});

export { getAdditionalPrescriptionInfoFormState, getFormValidationParameters, getIsPupillaryDistanceFileOptional, getIsRxAxisCylinderOptional, getManualPrescriptionValidationState, getPdUploadValidationRequirement, getPrescription, getPrescriptionType, getPrescriptionUploadValidationState, getSelectedAddOn, getSelectedLensType, getSelectedPackage, getValidationParameters, getVerificationAllowed };
//# sourceMappingURL=selectors.js.map
